/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import {
  ReactNode,
  useState,
  useCallback,
  createContext,
  useContext,
  useTransition,
} from "react";
import { NextIntlClientProvider } from "next-intl";
import { TLocale } from "@/i18n/config";
import { setUserLocale } from "@/services/locale";
import Loading from "../app/loading";

type I18nContextType = {
  changeLanguage: (locale: TLocale) => void;
  locale: TLocale;
};

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export function I18nProvider({
  children,
  locale: initialLocale,
  messages: initialMessages,
}: {
  children: ReactNode;
  locale: TLocale;
  messages: any;
}) {
  const [locale, setLocale] = useState(initialLocale);
  const [messages, setMessages] = useState(initialMessages);
  const [isPending, startTransition] = useTransition();

  const changeLanguage = useCallback(
    (newLocale: TLocale) => {
      startTransition(async () => {
        if (newLocale === locale) return;
        const msgs = await import(`../../messages/${newLocale}.json`);
        setLocale(newLocale);
        setMessages(msgs.default);

        document.documentElement.dir = newLocale === "ar" ? "rtl" : "ltr";
        document.documentElement.lang = newLocale;

        await setUserLocale(newLocale);
      });
    },
    [locale]
  );

  if (isPending) return <Loading />;

  return (
    <I18nContext.Provider value={{ changeLanguage, locale }}>
      <NextIntlClientProvider locale={locale} messages={messages}>
        {children}
      </NextIntlClientProvider>
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
